import React, { useState, useEffect } from 'react'
import styles from './MicrositeScriptEditorDrawer.module.css'
import { X, Save } from 'lucide-react'
import SelectInput from '../SelectInput/SelectInput'
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch'
import MicrositeCodeEditor from '../MicrositeCodeEditor/MicrositeCodeEditor'

const PLACEMENT_OPTIONS = [
    { value: 'HEAD', label: '<head>' },
    { value: 'BODY_START', label: '<body> start' },
    { value: 'BODY_END', label: '<body> end' }
]

const LOAD_STRATEGY_OPTIONS = [
    { value: 'ASYNC', label: 'Async (recommended)' },
    { value: 'DEFER', label: 'Defer' },
    { value: 'SYNC', label: 'Sync' }
]

const EMPTY_SCRIPT = {
    scriptName: '',
    scriptCode: '',
    placement: 'HEAD',
    loadStrategy: 'ASYNC',
    isActive: true
}

/**
 * MicrositeScriptEditorDrawer
 * Slide-in drawer for adding or editing a tracking/third-party
 * script — used on MicrositeTracking.
 *
 * Usage:
 *   <MicrositeScriptEditorDrawer
 *     open={drawerOpen}
 *     initialValue={editingScript}
 *     onClose={() => setDrawerOpen(false)}
 *     onSave={handleSaveScript}
 *     saving={saving}
 *   />
 */
const MicrositeScriptEditorDrawer = ({ open, initialValue, onClose, onSave, saving = false }) => {
    const [form, setForm] = useState(EMPTY_SCRIPT)

    useEffect(() => {
        if (open) setForm(initialValue ? { ...EMPTY_SCRIPT, ...initialValue } : EMPTY_SCRIPT)
    }, [open, initialValue])

    if (!open) return null

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

    return (
        <>
            <button type="button" className={styles.overlay} onClick={onClose} aria-label="Close" />
            <aside className={styles.drawer} role="dialog">
                <div className={styles.header}>
                    <h2 className={styles.title}>{initialValue ? 'Edit script' : 'Add tracking script'}</h2>
                    <button type="button" className={styles.closeBtn} onClick={onClose}>
                        <X size={17} />
                    </button>
                </div>

                <div className={styles.body}>
                    <label className={styles.label}>Script name</label>
                    <input
                        type="text"
                        className={styles.input}
                        value={form.scriptName}
                        onChange={(e) => update('scriptName', e.target.value)}
                        placeholder="e.g. Meta Pixel, Hotjar, Google Tag Manager"
                    />

                    <MicrositeCodeEditor
                        label="Script code"
                        mode="html"
                        value={form.scriptCode}
                        onChange={(v) => update('scriptCode', v)}
                        rows={7}
                        placeholder="<script>...</script>"
                    />

                    <div className={styles.row2}>
                        <SelectInput
                            label="Placement"
                            value={form.placement}
                            onChange={(v) => update('placement', v)}
                            options={PLACEMENT_OPTIONS}
                        />
                        <SelectInput
                            label="Load strategy"
                            value={form.loadStrategy}
                            onChange={(v) => update('loadStrategy', v)}
                            options={LOAD_STRATEGY_OPTIONS}
                        />
                    </div>

                    <ToggleSwitch
                        label="Active"
                        description="Script will be injected on your live site"
                        checked={form.isActive}
                        onChange={(v) => update('isActive', v)}
                    />
                </div>

                <div className={styles.footer}>
                    <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.saveBtn}
                        onClick={() => onSave?.(form)}
                        disabled={saving || !form.scriptName.trim() || !form.scriptCode.trim()}
                    >
                        <Save size={14} />
                        {saving ? 'Saving…' : 'Save script'}
                    </button>
                </div>
            </aside>
        </>
    )
}

export default MicrositeScriptEditorDrawer